/**
 * FlahaFast Redux Store Configuration
 *
 * Workflow slice is persisted to sessionStorage so Stage 1–3 data
 * survives refresh within the same browser tab (P0).
 */

import { configureStore, type UnknownAction } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import waterQualityReducer from './slices/waterQualitySlice';
import saltLibraryReducer from './slices/saltLibrarySlice';
import formulationReducer from './slices/formulationSlice';
import calculationReducer from './slices/calculationSlice';
import resultsReducer from './slices/resultsSlice';
import elementsReducer from './slices/elementSlice';
import cropRecommendationReducer from './slices/cropRecommendationSlice';
import authReducer from './slices/authSlice';
import subscriptionReducer from './slices/subscriptionSlice';
import workflowReducer from './slices/workflowSlice';
import recipeDraftReducer from './slices/recipeDraftSlice';
import { clearPersistedWorkflow, loadWorkflowState, saveWorkflowState } from './workflowPersistence';

const combinedReducer = combineReducers({
  auth: authReducer,
  subscription: subscriptionReducer,
  workflow: workflowReducer,
  recipeDraft: recipeDraftReducer,
  waterQuality: waterQualityReducer,
  saltLibrary: saltLibraryReducer,
  formulation: formulationReducer,
  calculation: calculationReducer,
  results: resultsReducer,
  elements: elementsReducer,
  cropRecommendation: cropRecommendationReducer,
});

export type RootState = ReturnType<typeof combinedReducer>;

export function rootReducer(state: RootState | undefined, action: UnknownAction): RootState {
  const endSession = ['auth/logout/pending', 'auth/logout/fulfilled',
    'auth/getCurrentUser/rejected', 'auth/refreshToken/rejected'].includes(action.type);
  const beginLogin = ['auth/login/pending', 'auth/register/pending'].includes(action.type);
  if (endSession || beginLogin) {
    clearPersistedWorkflow();
    const empty = combinedReducer(undefined, { type: '@@init' });
    state = { ...empty, auth: { ...empty.auth, token: null, isInitialized: true } };
  }
  let next = combinedReducer(state, action);
  if (['auth/getCurrentUser/fulfilled', 'auth/login/fulfilled', 'auth/register/fulfilled'].includes(action.type) &&
      next.auth.user?.id !== state?.auth.user?.id) {
    const saved = next.auth.user?.id ? loadWorkflowState(next.auth.user.id) : undefined;
    const empty = combinedReducer(undefined, { type: '@@init' });
    next = { ...empty, auth: next.auth, workflow: saved || empty.workflow };
  }
  return next;
}

const store = configureStore({
  reducer: rootReducer,
});

store.subscribe(() => {
  const { auth, workflow } = store.getState();
  if (auth.isAuthenticated && auth.user) saveWorkflowState(auth.user.id, workflow);
  else if (auth.isInitialized) clearPersistedWorkflow();
});

export type AppDispatch = typeof store.dispatch;

export default store;
